import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, Cell, ReferenceLine,
} from 'recharts';
import api from '../utils/api';

const TARGET_OPTIONS = [
  { label: '−30% by 2030', value: 'target_30' },
  { label: '−50% by 2030', value: 'target_50' },
  { label: 'Net-zero by 2040', value: 'netzero_40' },
  { label: 'Custom', value: 'custom' },
];

const STATUS_COLORS  = { complete: 'var(--neon-green)', in_progress: 'var(--neon-yellow)', upcoming: 'var(--muted2)' };
const STATUS_LABELS  = { complete: '✓ Done', in_progress: '→ In Progress', upcoming: '○ Upcoming' };

export default function Roadmap() {
  const [roadmap, setRoadmap]     = useState(null);
  const [score, setScore]         = useState(null);
  const [target, setTarget]       = useState('target_50');
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/emissions/roadmap').then((r) => setRoadmap(r.data)).catch(() => {}),
      api.get('/emissions/score').then((r) => setScore(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const milestones   = roadmap?.milestones || [];
  const maccItems    = roadmap?.maccItems || [];
  const projectedCurve = roadmap?.projectedCurve || [];
  const targetCurve    = roadmap?.targetCurve || [];
  const actualCurve    = roadmap?.actualCurve || [];

  // Merge curves for chart
  const chartData = projectedCurve.map((p) => {
    const t = targetCurve.find((x) => x.year === p.year);
    const a = actualCurve.find((x) => x.year === p.year);
    return { year: p.year, projected: p.tco2e, target: t?.tco2e, actual: a?.tco2e };
  });

  const maxMacc = maccItems.length > 0 ? Math.max(...maccItems.map((m) => Math.abs(m.cost_per_tonne_inr))) : 1;

  return (
    <div className="page-shell page-stack">
      <section className="section-stack">
        <div className="section-tag">Net-Zero Planner — Decarbonization Roadmap</div>
        <h1>Net-Zero Roadmap</h1>
        <p className="section-copy">Science-based decarbonization planning. Set targets aligned to SBTi 1.5°C pathway, visualise your reduction curve, and prioritise interventions by cost.</p>
      </section>

      {/* Target Setting */}
      <section className="surface-panel glow-surface" style={{ padding: '1.5rem' }}>
        <div className="section-tag" style={{ marginBottom: '1rem' }}>Net-zero target setting</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2rem', alignItems: 'center' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
            {TARGET_OPTIONS.map((t) => (
              <button key={t.value} type="button"
                className={`btn btn-sm ${target === t.value ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setTarget(t.value)}
                style={{ justifyContent: 'center' }}>
                {t.label}
              </button>
            ))}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="stat-label">Aligned with</div>
            <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.78rem', color: 'var(--neon-cyan)', marginTop: '0.2rem' }}>SBTi 1.5°C pathway</div>
          </div>
        </div>
        {score && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1.5rem', marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid var(--border)' }}>
            {[
              { label: 'Baseline (2025)', value: `${score.gross_emissions_tco2e || 450} tCO2e`, color: 'var(--neon-orange)' },
              { label: 'Current score', value: `${score.score || 67}/100`, color: 'var(--neon-cyan)' },
              { label: 'YoY reduction', value: `${score.reduction_pct || 0}%`, color: 'var(--neon-green)' },
              { label: 'Net emissions', value: `${score.net_emissions_tco2e || 0} tCO2e`, color: 'var(--text)' },
            ].map((s) => (
              <div key={s.label}>
                <div className="stat-label">{s.label}</div>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.95rem', color: s.color, marginTop: '0.2rem' }}>{s.value}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Emissions Curve */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-tag" style={{ marginBottom: '1.25rem' }}>Projected emissions pathway (tCO2e)</div>
        {loading ? (
          <div className="stat-sub">Loading…</div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="year" tick={{ fill: 'var(--muted2)', fontSize: 11, fontFamily: 'Space Mono, monospace' }} />
              <YAxis tick={{ fill: 'var(--muted2)', fontSize: 11, fontFamily: 'Space Mono, monospace' }} />
              <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.72rem' }} />
              <Legend wrapperStyle={{ fontSize: '0.72rem', fontFamily: 'Space Mono, monospace', color: 'var(--muted2)' }} />
              <Line type="monotone" dataKey="projected" name="BAU (no action)" stroke="var(--neon-pink)" strokeWidth={2} dot={false} strokeDasharray="5 3" />
              <Line type="monotone" dataKey="target" name="Target pathway" stroke="var(--neon-green)" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="actual" name="Actual" stroke="var(--neon-cyan)" strokeWidth={2} dot={{ r: 3, fill: 'var(--neon-cyan)' }} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div className="stat-sub">Log emission activities to see your pathway curve. <Link to="/emissions" style={{ color: 'var(--neon-cyan)' }}>Start logging →</Link></div>
          </div>
        )}
      </section>

      {/* Milestone Cards */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-tag" style={{ marginBottom: '1.25rem' }}>Decarbonization milestones</div>
        {milestones.length === 0 ? (
          <div className="stat-sub">No milestones found. Run a roadmap computation from the emissions module.</div>
        ) : (
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {milestones.map((m) => (
              <div key={m.id || m.year} style={{ display: 'grid', gridTemplateColumns: '80px 1fr auto auto', gap: '1.5rem', alignItems: 'center', padding: '1.25rem', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: `1px solid ${STATUS_COLORS[m.status] || 'var(--border)'}22` }}>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '1.4rem', color: STATUS_COLORS[m.status] || 'var(--muted2)' }}>{m.year}</div>
                <div>
                  <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '0.88rem', marginBottom: '0.3rem' }}>{m.label || `Target: ${m.target_tco2e} tCO2e`}</div>
                  <div className="stat-sub">{m.key_action}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div className="stat-label">Target</div>
                  <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.85rem', color: 'var(--neon-orange)', marginTop: '0.2rem' }}>{m.target_tco2e} tCO2e</div>
                  {m.budget_inr > 0 && <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.68rem', color: 'var(--muted2)', marginTop: '0.15rem' }}>₹{(m.budget_inr / 100000).toFixed(1)}L</div>}
                </div>
                <span className="badge" style={{ color: STATUS_COLORS[m.status], borderColor: STATUS_COLORS[m.status], fontSize: '0.6rem', whiteSpace: 'nowrap' }}>
                  {STATUS_LABELS[m.status]}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* MACC Curve */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-tag" style={{ marginBottom: '0.5rem' }}>Marginal Abatement Cost Curve (MACC)</div>
        <p className="stat-sub" style={{ marginBottom: '1.25rem' }}>Interventions sorted by cost per tonne CO2e removed. Negative = cost-saving. Bar width ∝ reduction potential.</p>
        {maccItems.length === 0 ? (
          <div className="stat-sub">MACC data unavailable. Ensure roadmap endpoint is reachable.</div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={maccItems} layout="vertical" margin={{ top: 0, right: 20, left: 180, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                <XAxis type="number" tick={{ fill: 'var(--muted2)', fontSize: 10, fontFamily: 'Space Mono, monospace' }} label={{ value: '₹ / tCO2e', position: 'insideBottomRight', offset: -5, fill: 'var(--muted2)', fontSize: 10 }} />
                <YAxis type="category" dataKey="intervention" tick={{ fill: 'var(--text)', fontSize: 10, fontFamily: '"Plus Jakarta Sans", sans-serif' }} width={175} />
                <Tooltip
                  formatter={(v, n) => [`₹${v.toLocaleString('en-IN')} / tCO2e`, 'Cost']}
                  contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.72rem' }} />
                <ReferenceLine x={0} stroke="var(--border)" strokeWidth={2} />
                <Bar dataKey="cost_per_tonne_inr" name="Cost (₹/tCO2e)" radius={[0, 4, 4, 0]}>
                  {maccItems.map((entry, i) => (
                    <Cell key={i} fill={entry.cost_per_tonne_inr <= 0 ? 'var(--neon-green)' : 'var(--neon-orange)'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem', marginTop: '1.25rem' }}>
              {maccItems.map((m, i) => (
                <div key={i} style={{ padding: '0.75rem 1rem', background: 'rgba(255,255,255,0.02)', borderRadius: '6px', border: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '0.82rem', fontFamily: '"Plus Jakarta Sans", sans-serif', marginBottom: '0.25rem' }}>{m.intervention}</div>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.68rem', color: 'var(--muted2)' }}>−{Math.round((m.reduction_potential_pct || 0) * 100)}% emissions</span>
                      <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.68rem', color: m.roi_months <= 6 ? 'var(--neon-green)' : m.roi_months <= 18 ? 'var(--neon-yellow)' : 'var(--neon-pink)' }}>{m.roi_months}mo ROI</span>
                    </div>
                  </div>
                  <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.85rem', color: m.cost_per_tonne_inr <= 0 ? 'var(--neon-green)' : 'var(--neon-orange)', textAlign: 'right' }}>
                    {m.cost_per_tonne_inr <= 0 ? '' : '+'}{m.cost_per_tonne_inr?.toLocaleString('en-IN')}
                    <div style={{ fontSize: '0.6rem', color: 'var(--muted2)' }}>₹/tCO2e</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Active Initiatives */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-header-row" style={{ marginBottom: '1.25rem' }}>
          <div className="section-tag">Active reduction initiatives</div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Link to="/services/logistics" className="btn btn-secondary btn-sm">+ Logistics</Link>
            <Link to="/services/packaging" className="btn btn-secondary btn-sm">+ Packaging</Link>
            <Link to="/emissions" className="btn btn-primary btn-sm">Log emission →</Link>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
          {[
            { title: 'Renewable energy switch', module: 'Energy', status: 'in_progress', reduction: '35%', deadline: 'Q2 2026', link: '/services/procurement' },
            { title: 'Fleet electrification (30%)', module: 'Logistics', status: 'upcoming', reduction: '12%', deadline: 'Q4 2026', link: '/services/logistics' },
            { title: 'Mono-PE packaging switch', module: 'Packaging', status: 'upcoming', reduction: '8%', deadline: 'Q1 2026', link: '/services/packaging' },
            { title: 'Supplier engagement program', module: 'Supply Chain', status: 'in_progress', reduction: '20%', deadline: 'Q3 2026', link: '/supply-chain' },
          ].map((init, i) => (
            <Link key={i} to={init.link} style={{ textDecoration: 'none' }}>
              <div style={{ padding: '1.25rem', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid var(--border)', cursor: 'pointer', transition: 'border-color 0.2s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                  <span className="badge" style={{ fontSize: '0.58rem', color: 'var(--neon-cyan)', borderColor: 'var(--neon-cyan)' }}>{init.module}</span>
                  <span className="badge" style={{ fontSize: '0.58rem', color: STATUS_COLORS[init.status], borderColor: STATUS_COLORS[init.status] }}>{STATUS_LABELS[init.status]}</span>
                </div>
                <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '0.88rem', marginBottom: '0.5rem' }}>{init.title}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div><div className="stat-label">Reduction</div><div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.8rem', color: 'var(--neon-green)' }}>-{init.reduction}</div></div>
                  <div><div className="stat-label">Deadline</div><div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.78rem', color: 'var(--muted2)' }}>{init.deadline}</div></div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
